import {
  addDays,
  daysBetween,
  isSchoolFree,
  nextDates,
  schoolHolidaysBetween,
  suggestIcon,
  WEEKDAY_LONG,
  WEEKDAY_SHORT,
  weekdayOf,
  weekdayOfMonth,
  type Assignees,
  type CalendarEvent,
  type SeriesRule,
  type Spot,
} from '@shh/shared';
import { Bell, CalendarBlank, Clock, DotsThreeCircle, MapPin, Repeat } from '@phosphor-icons/react';
import {
  ChoiceGroup,
  cn,
  FormSection,
  MonthPicker,
  MultiChoice,
  Switch,
  Text,
  TextField,
} from '@shh/ui';
import { useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { useLens } from '../app/lens';
import { endSeriesBefore, saveEvent, skipOccurrence } from '../data/api';
import { dayLabel } from '../data/agenda';
import type { E } from '../data/derive';
import { useFamily } from '../data/family';
import { formsText } from '../i18n/forms';
import { dayTitle, dayTitleShort, monthName, monthTitle, rangeText } from './calendar/format';
import { SpotSearch } from './Spot';
import { locale } from '../lib/locale';
import {
  Actions,
  defaultRemindLabel,
  fromRemindChoice,
  RemindField,
  StarAmount,
  TitleField,
  toRemindChoice,
  useSave,
  WhoField,
  type RemindChoice,
} from './forms';

/*
 * The event form of Kalender 2 (1.25, prototype approved by Mike 20.10.): one event – a series is
 * an event plus "Wiederholen" (Mike: "Hat eine Serie nicht immer alle Angaben von einem Termin, nur
 * plus zusätzlich die Wiederholungseinstellungen?"). Start and end in one go, all day for several
 * days, every rhythm, a pause in the school holidays, stars folded away.
 */

type Freq = 'none' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'yearly';
type End = 'never' | 'summer' | 'date' | 'count';
type Scope = 'one' | 'following' | 'all';
type Open = 'day' | 'repeat' | 'place' | 'remind' | 'more' | null;

const toMin = (t: string) => t.split(':').reduce((a, b) => a * 60 + Number(b), 0);
const fromMin = (m: number) => {
  const x = ((m % 1440) + 1440) % 1440;
  return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
};

/** The stored rule → the form's choices. */
function freqOf(r?: SeriesRule): Freq {
  if (!r) return 'none';
  if (r.freq === 'daily') return 'daily';
  if (r.freq === 'monthly') return 'monthly';
  if (r.freq === 'yearly') return 'yearly';
  return [...r.days].sort().join() === '1,2,3,4,5' && (r.every ?? 1) === 1 ? 'weekdays' : 'weekly';
}

export function EventForm({
  initial: shown,
  draft,
  onDone,
  onSaved,
}: {
  /** An event as tapped – for a series: that day's copy (its date is the day tapped). */
  initial?: E;
  /** a new event, filled in beforehand ("Einplanen" from an idea) */
  draft?: Partial<E>;
  onDone: () => void;
  /** after saving, with the day it was saved for */
  onSaved?: (date: string) => Promise<unknown> | void;
}) {
  const f = useFamily();
  const lens = useLens();
  const region = f.household?.location?.region;
  const schoolFree = region ? (d: string) => isSchoolFree(region, d) : undefined;
  // a series is shown per day – the stored series holds its rule and first day
  const series = shown?.repeat ? (f.events.find((e) => e.id === shown.id) ?? shown) : undefined;
  const seed = shown ?? draft;
  // a new event may come with its series ("Einfach sagen", 1.27)
  const rule = series?.repeat ?? draft?.repeat;

  const [title, setTitle] = useState(seed?.title ?? '');
  const [who, setWho] = useState<Assignees>(seed?.assignees ?? 'all');
  const [date, setDate] = useState(seed?.date ?? f.today);
  const [endDate, setEndDate] = useState(seed?.endDate ?? seed?.date ?? f.today);
  const [allDay, setAllDay] = useState(seed ? !seed.start : false);
  const [start, setStart] = useState(seed?.start ?? '16:00');
  const [end, setEnd] = useState(seed?.end ?? fromMin(toMin(seed?.start ?? '16:00') + 60));
  const [freq, setFreq] = useState<Freq>(freqOf(rule));
  const [every, setEvery] = useState(String(rule?.every ?? 1));
  const [days, setDays] = useState<string[]>(
    (rule?.days?.length ? rule.days : [weekdayOf(seed?.date ?? f.today)]).map(String),
  );
  const [monthBy, setMonthBy] = useState<'date' | 'weekday'>(rule?.monthBy ?? 'date');
  const [endKind, setEndKind] = useState<End>(
    rule?.count ? 'count' : rule?.until ? 'date' : 'never',
  );
  const [until, setUntil] = useState(rule?.until ?? addDays(seed?.date ?? f.today, 90));
  const [count, setCount] = useState(String(rule?.count ?? 10));
  const [pause, setPause] = useState(Boolean(rule?.skipHolidays));
  const [location, setLocation] = useState(seed?.location ?? '');
  const [transport, setTransport] = useState(seed?.transport ?? '');
  const [bring, setBring] = useState((seed?.bring ?? []).join(', '));
  const [notes, setNotes] = useState(seed?.notes ?? '');
  const [stars, setStars] = useState(String(seed?.stars ?? 0));
  const [remind, setRemind] = useState<RemindChoice>(toRemindChoice(seed?.remind));
  const [asking, setAsking] = useState(false);
  // a new event walks through day → time; one picked in the calendar starts at the time
  const [open, setOpen] = useState<Open>(shown || draft?.date ? null : 'day');
  // the time opens with the first day picked and stays open next to the month
  const [timeOpen, setTimeOpen] = useState(!shown && !!draft?.date);
  const [rangeDone, setRangeDone] = useState(true);
  const [spot, setSpot] = useState<Spot>();
  const { busy, error, save } = useSave(onDone);

  // the summer holidays of the family's state, for "Endet: zu den Sommerferien"
  const summer = region
    ? schoolHolidaysBetween(region, date, addDays(date, 400), locale).find(
        (h) => h.key === 'summer',
      )
    : undefined;

  const repeat = (): SeriesRule | undefined => {
    if (freq === 'none') return undefined;
    const n = Math.max(1, Number(every) || 1);
    const base: SeriesRule =
      freq === 'daily'
        ? { freq: 'daily', days: [1, 2, 3, 4, 5, 6, 7] }
        : freq === 'weekdays'
          ? { days: [1, 2, 3, 4, 5] }
          : freq === 'weekly'
            ? { days: days.map(Number).sort() }
            : freq === 'monthly'
              ? { freq: 'monthly', days: [], monthBy }
              : { freq: 'yearly', days: [] };
    // Firestore refuses nested undefined – keys only when they are set
    return {
      ...base,
      ...(n > 1 && freq !== 'weekdays' && freq !== 'yearly' ? { every: n } : {}),
      ...(endKind === 'summer' && summer ? { until: addDays(summer.start, -1) } : {}),
      ...(endKind === 'date' && until >= date ? { until } : {}),
      ...(endKind === 'count' ? { count: Math.max(1, Number(count) || 1) } : {}),
      ...(pause ? { skipHolidays: true } : {}),
      ...(rule?.skip?.length ? { skip: rule.skip } : {}),
    };
  };

  const fields = (
    r: SeriesRule | undefined,
    day: string,
  ): Omit<CalendarEvent, 'id' | 'source'> => ({
    title: title.trim(),
    icon: suggestIcon(title, 'calendar'),
    date: day,
    // several days: all day, or from a start time to an end time days later
    ...(!r && endDate > day ? { endDate } : {}),
    repeat: r,
    recurring: r ? true : undefined,
    start: allDay ? undefined : start,
    end: allDay ? undefined : end,
    assignees: who,
    location: location.trim() || undefined,
    transport: transport.trim() || undefined,
    bring: bring
      .split(',')
      .map((b) => b.trim())
      .filter(Boolean),
    notes: notes.trim() || undefined,
    remind: fromRemindChoice(remind),
    // stars come from parents only (the rules refuse them otherwise)
    stars: lens.canParent && Number(stars) > 0 ? Math.min(20, Number(stars)) : undefined,
  });

  const store = (scope?: Scope) =>
    save(async () => {
      const r = repeat();
      if (series && shown && scope === 'one') {
        // only this date: it leaves the series and lives on as a single event
        await skipOccurrence(f.householdId, series.id, shown.date);
        await saveEvent(f.householdId, fields(undefined, date));
      } else if (series && shown && scope === 'following' && shown.date > series.date) {
        // from this date on: the old series ends the day before, a new one starts here
        await endSeriesBefore(f.householdId, series.id, shown.date);
        const rest =
          r?.count !== undefined
            ? {
                ...r,
                count: Math.max(
                  1,
                  r.count -
                    nextDates(series.date, r, series.date, 500, { schoolFree }).filter(
                      (x) => x.date < shown.date,
                    ).length,
                ),
              }
            : r;
        await saveEvent(f.householdId, fields(rest ? { ...rest, skip: undefined } : rest, date));
      } else if (series) {
        // the whole series: its first day stays, unless the day itself was changed
        const day = shown && date !== shown.date ? date : series.date;
        await saveEvent(f.householdId, { id: series.id, ...fields(r, day) });
      } else {
        await saveEvent(f.householdId, { id: shown?.id, ...fields(r, date) });
      }
      await onSaved?.(date);
    });

  const r = repeat();
  const preview = r ? nextDates(date, r, date, 8, { schoolFree }) : [];
  const weekdayName = WEEKDAY_LONG[locale][weekdayOf(date) - 1] ?? '';
  const nth = formsText.ordinals[Math.min(5, weekdayOfMonth(date)) - 1] ?? '';
  const valid =
    title.trim().length > 0 &&
    (allDay || endDate > date || toMin(end) !== toMin(start)) &&
    (freq !== 'weekly' || days.length > 0);

  if (asking && shown)
    return (
      <div className="flex flex-col gap-gap-md">
        <Text className="font-bold">{formsText.scopeTitle}</Text>
        {(
          [
            [
              'one',
              formsText.scopeOne,
              formsText.scopeOneHint(dayLabel(f, shown.date, true).label),
            ],
            [
              'following',
              formsText.scopeFollowing,
              formsText.scopeFollowingHint(dayLabel(f, shown.date, true).label),
            ],
            ['all', formsText.scopeAll, formsText.scopeAllHint],
          ] as const
        ).map(([scope, label, hint]) => (
          <AriaButton
            key={scope}
            onPress={() => store(scope)}
            isDisabled={busy}
            className="flex min-h-touch-target cursor-pointer flex-col items-start rounded-card border-2 border-border-default bg-surface-raised p-inset-sm text-left outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-99"
          >
            <span className="font-bold">{label}</span>
            <span className="text-sm text-text-secondary">{hint}</span>
          </AriaButton>
        ))}
        {error && (
          <p role="alert" className="font-semibold text-feedback-danger">
            {error}
          </p>
        )}
      </div>
    );

  const repeatDetails = (
    <div className="flex flex-col gap-gap-lg">
      {(freq === 'daily' || freq === 'weekly' || freq === 'monthly') && (
        <ChoiceGroup
          label={formsText.howOften}
          value={every}
          onChange={setEvery}
          choices={(freq === 'monthly'
            ? ['1', '2', '3', '6']
            : freq === 'daily'
              ? ['1', '2', '3']
              : ['1', '2', '3', '4']
          ).map((n) => ({
            value: n,
            label:
              freq === 'daily'
                ? n === '1'
                  ? formsText.everyDay
                  : formsText.everyNDays(n)
                : freq === 'weekly'
                  ? n === '1'
                    ? formsText.everyWeek
                    : formsText.everyNWeeks(n)
                  : n === '1'
                    ? formsText.everyMonth
                    : formsText.everyNMonths(n),
          }))}
        />
      )}
      {freq === 'weekly' && (
        <MultiChoice
          label={formsText.whichDays}
          description={formsText.whichDaysHint}
          value={days}
          onChange={setDays}
          choices={WEEKDAY_SHORT[locale].map((d, i) => ({ value: String(i + 1), label: d }))}
        />
      )}
      {freq === 'monthly' && (
        <ChoiceGroup<'date' | 'weekday'>
          label={formsText.onWhichDay}
          value={monthBy}
          onChange={setMonthBy}
          choices={[
            { value: 'date', label: formsText.monthDate(String(Number(date.slice(8)))) },
            { value: 'weekday', label: formsText.monthWeekday(nth, weekdayName) },
          ]}
        />
      )}
      <div className="flex flex-col gap-gap-sm">
        <ChoiceGroup<End>
          label={formsText.ends}
          value={endKind}
          onChange={setEndKind}
          choices={[
            { value: 'never', label: formsText.never },
            ...(summer ? [{ value: 'summer' as const, label: formsText.endsSummer }] : []),
            { value: 'date', label: formsText.endsOn },
            { value: 'count', label: formsText.endsAfter },
          ]}
        />
        {endKind === 'date' && (
          <TextField
            label={formsText.endsOnLabel}
            type="date"
            value={until}
            onChange={(v) => v && setUntil(v)}
          />
        )}
        {endKind === 'count' && (
          <TextField
            label={formsText.countLabel}
            type="number"
            inputMode="numeric"
            value={count}
            onChange={(v) => setCount(v.replace(/[^0-9]/g, ''))}
          />
        )}
      </div>
      {region && (
        <MultiChoice
          label={formsText.holidays}
          description={formsText.pauseHint}
          value={pause ? ['pause'] : []}
          onChange={(v) => setPause(v.includes('pause'))}
          choices={[{ value: 'pause', label: formsText.pauseHolidays }]}
        />
      )}
      {preview.length > 0 && (
        <div className="flex flex-col gap-gap-xs">
          <Text size="sm" tone="secondary">
            {formsText.nextDates}
            {pause ? ` (${formsText.pausedHint})` : ''}
          </Text>
          <ul className="flex flex-wrap gap-gap-xs">
            {preview.map((x) => (
              <li
                key={x.date}
                className={cn(
                  'rounded-control border border-border-subtle px-inset-xs py-0.5 text-sm font-semibold tabular-nums',
                  x.paused && 'bg-feedback-warning-surface text-text-muted line-through',
                )}
              >
                {dayTitleShort(x.date)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
  const toggle = (k: Open) => (o: boolean) => {
    setOpen(o ? k : null);
    // leaving the month after one tap keeps that one day
    setRangeDone(true);
  };
  const len = (toMin(end) - toMin(start) + 1440) % 1440;
  const pickDay = (d: string) => {
    setDate(d);
    if (endDate < d) setEndDate(d);
    // a new start day suggests its weekday, as long as nothing else was picked
    if (days.length <= 1) setDays([String(weekdayOf(d))]);
  };
  const repeatSummary =
    freq === 'weekly'
      ? `${formsText.weekly} · ${days.map((d) => WEEKDAY_SHORT[locale][Number(d) - 1]).join(', ')}`
      : {
          none: formsText.never,
          daily: formsText.daily,
          weekdays: formsText.weekdays,
          monthly: formsText.monthly,
          yearly: formsText.yearly,
        }[freq];
  const extras = [
    transport.trim() && formsText.transport,
    bring.trim() && formsText.bring,
    notes.trim() && formsText.note,
    Number(stars) > 0 && `+${stars} ⭐`,
  ].filter(Boolean);

  return (
    <form className="flex flex-col gap-gap-md" onSubmit={(e) => e.preventDefault()}>
      <TitleField
        label={formsText.what}
        placeholder={formsText.eventExample}
        value={title}
        onChange={setTitle}
        fallback="calendar"
      />
      <div className="flex flex-col">
        <FormSection
          icon={CalendarBlank}
          label={formsText.day}
          summary={endDate > date ? rangeText(date, endDate) : dayTitleShort(date)}
          isOpen={open === 'day'}
          onOpenChange={toggle('day')}
        >
          {/* first the day – or several: tap the first, then the last (Mike, 21.10.) */}
          {freq === 'none' && (
            <Text size="sm" tone="secondary">
              {rangeDone ? formsText.dayOrRange : formsText.pickLastDay}
            </Text>
          )}
          <MonthPicker
            value={date}
            end={freq === 'none' ? endDate : undefined}
            today={f.today}
            weekdays={[...WEEKDAY_SHORT[locale]]}
            monthLabel={monthTitle}
            dayLabel={dayTitle}
            prevLabel={formsText.prevMonth}
            nextLabel={formsText.nextMonth}
            monthName={monthName}
            pickMonthLabel={formsText.pickMonth}
            onPick={(d) => {
              // first tap = the first day; then the last – or the same day again for just
              // that day (Mike, 21.10.). A series has one start day.
              if (freq === 'none' && (rangeDone || d < date)) {
                pickDay(d);
                setEndDate(d);
                setRangeDone(false);
                return;
              }
              if (freq === 'none') setEndDate(d);
              else {
                pickDay(d);
                setEndDate(d);
              }
              setRangeDone(true);
              setOpen(null);
              if (!allDay) setTimeOpen(true);
            }}
          />
          {endDate > date && (
            <Text size="sm" tone="muted">
              {formsText.multiDay(String(daysBetween(date, endDate) + 1))}
            </Text>
          )}
        </FormSection>
        <FormSection
          icon={Clock}
          label={formsText.time}
          summary={allDay ? formsText.allDay : `${start} – ${end}`}
          isOpen={timeOpen}
          onOpenChange={setTimeOpen}
        >
          {/* the times refer to these days – said small, so it is clear (Mike, 21.10.) */}
          <Text size="sm" tone="muted">
            {endDate > date
              ? formsText.timesFor(dayTitleShort(date), dayTitleShort(endDate))
              : dayTitle(date)}
          </Text>
          {!allDay && (
            <div className="grid grid-cols-2 gap-gap-md">
              <TextField
                label={formsText.from}
                type="time"
                value={start}
                onChange={(v) => {
                  if (!v) return;
                  // on one day the end moves along – the length stays
                  setStart(v);
                  if (endDate === date) setEnd(fromMin(toMin(v) + len));
                }}
              />
              <TextField
                label={formsText.to}
                type="time"
                value={end}
                onChange={(v) => v && setEnd(v)}
              />
            </div>
          )}
          <Switch isSelected={allDay} onChange={setAllDay}>
            {formsText.allDay}
          </Switch>
        </FormSection>
      </div>
      <WhoField
        label={formsText.forWhom}
        value={who}
        onChange={setWho}
        people={f.members}
        layout="person"
      />
      <div className="flex flex-col">
        <FormSection
          icon={Repeat}
          label={formsText.repeat}
          summary={repeatSummary}
          isOpen={open === 'repeat'}
          onOpenChange={toggle('repeat')}
        >
          <ChoiceGroup<Freq>
            label={formsText.repeat}
            value={freq}
            onChange={(v) => {
              setFreq(v);
              setEvery('1');
            }}
            choices={[
              { value: 'none', label: formsText.never },
              { value: 'daily', label: formsText.daily },
              { value: 'weekdays', label: formsText.weekdays },
              { value: 'weekly', label: formsText.weekly },
              { value: 'monthly', label: formsText.monthly },
              { value: 'yearly', label: formsText.yearly },
            ]}
          />
          {freq !== 'none' && repeatDetails}
        </FormSection>
        <FormSection
          icon={MapPin}
          label={formsText.place}
          summary={location}
          placeholder={formsText.optional}
          isOpen={open === 'place'}
          onOpenChange={toggle('place')}
        >
          <TextField
            label={formsText.where}
            description={formsText.placeHint}
            value={location}
            onChange={(v) => {
              setLocation(v);
              setSpot(undefined);
            }}
          />
          <SpotSearch
            query={location}
            spot={spot}
            onPick={({ spot: s }) => {
              setSpot(s);
              setLocation([s.name, s.address].filter(Boolean).join(', '));
            }}
            onClear={() => {
              setSpot(undefined);
              setLocation('');
            }}
          />
        </FormSection>
        <FormSection
          icon={Bell}
          label={formsText.remind}
          summary={
            {
              default: defaultRemindLabel(f, allDay),
              '10': formsText.min10,
              '30': '30',
              '60': formsText.hour1,
              eve: formsText.eve,
              none: formsText.none,
            }[remind]
          }
          placeholder={formsText.remind}
          isOpen={open === 'remind'}
          onOpenChange={toggle('remind')}
        >
          <RemindField
            value={remind}
            onChange={(v) => {
              setRemind(v);
              setOpen(null);
            }}
            defaultLabel={defaultRemindLabel(f, allDay)}
          />
        </FormSection>
        <FormSection
          icon={DotsThreeCircle}
          label={formsText.extras}
          summary={extras.join(' · ')}
          placeholder={formsText.extrasHint}
          isOpen={open === 'more'}
          onOpenChange={toggle('more')}
        >
          <TextField
            label={formsText.transport}
            placeholder={formsText.transportExample}
            value={transport}
            onChange={setTransport}
          />
          <TextField
            label={formsText.bring}
            description={formsText.bringHint}
            placeholder={formsText.bringExample}
            value={bring}
            onChange={setBring}
          />
          <TextField label={formsText.note} value={notes} onChange={setNotes} />
          {lens.canParent && (
            <StarAmount
              label={formsText.starsEvent}
              presets={['0', '2', '5', '10']}
              format={(p) => (p === '0' ? formsText.noStars : '+' + p)}
              value={stars}
              onChange={setStars}
              min={0}
              max={20}
            />
          )}
        </FormSection>
      </div>
      <Actions
        onCancel={onDone}
        saveLabel={shown ? formsText.save : formsText.addEvent}
        disabled={!valid}
        busy={busy}
        error={error}
        // a series asks first: only this date, from here on, or all of it
        onSave={() => (series && shown ? setAsking(true) : store())}
      />
    </form>
  );
}
